import DropDownButtonLegacy from 'common/components/drop-downs/legacy/dropDownButtonLegacy';
import SimpleButtonContainerContent from 'common/components/drop-downs/legacy/simpleDropDownButtonContainerContent';
import Textbox from 'common/components/inputs/textbox';
import { useDispatch, useSelector } from 'react-redux';
import {
  onTargetRemoved,
  selectRuleTargetsByRuleId,
  updateRuleTarget
} from '../segmentation-config.slice';
import { RuleTarget, TargetType } from '../segmentation-config.types';
import { DropDownItemType } from 'common/components/drop-downs/legacy/dropDownItemType';
import Button from 'common/components/buttons/button';
import { ButtonTheme } from 'common/components/buttons/button-theme';
import { RootState } from 'app/store';

interface SegmentationConfigRuleTargetProps {
  target: RuleTarget;
  targetTypeDropDownItems: DropDownItemType[];
  operatorDropDownItems: DropDownItemType[];
}

export default function SegmentationConfigRuleTarget({
  target,
  targetTypeDropDownItems,
  operatorDropDownItems
}: SegmentationConfigRuleTargetProps) {
  const dispatch = useDispatch();
  const targets = useSelector((state: RootState) =>
    selectRuleTargetsByRuleId(state, target.rule_id)
  );

  const updateTarget = (ruleKey: number, targetKey: number, props: any) => {
    dispatch(updateRuleTarget({ ruleKey, targetKey, props }));
  };

  const removeTarget = (targetKey: number, ruleKey: number) => {
    dispatch(onTargetRemoved({ ruleKey, targetKey }));
  };

  const canRemoveTarget = (ruleKey: number) => {
    return targets.filter((item) => item.rule_id === ruleKey).length > 1;
  };

  const isRuleTargetKeyValue = (target: RuleTarget): boolean => {
    return (
      target.type === TargetType.PAYLOAD_KEY_AND_VALUE ||
      target.type ===
        TargetType.LANDING_PAGE_URL_AND_QUERY_STRING_SEGMENTS_KEY_AND_VALUE
    );
  };

  return (
    <div className="mb-4 last:mb-0 mt-2">
      {/* Three-column layout with proper alignment */}
      <div className="flex flex-col lg:flex-row justify border rounded border-neutral-200 p-4 gap-3">
        {/* Target Type Dropdown */}
        <div className="flex flex-col">
          <DropDownButtonLegacy
            dropDirection={'left'}
            dropDownItems={targetTypeDropDownItems}
            selectDropDownItem={(value) => {
              updateTarget(target.rule_id, target.target_id, { type: value });
            }}
          >
            <SimpleButtonContainerContent
              additionalContainerStyling="bg-white rounded border border-neutral-200"
              widthClass="w-full 3xl:w-[300px]"
              dropdownLabel={
                targetTypeDropDownItems.find((item) => item.key === target.type)
                  ?.label ?? targetTypeDropDownItems[0].label
              }
            />
          </DropDownButtonLegacy>
        </div>

        {/* Operator Dropdown */}
        <div className="flex flex-col">
          <DropDownButtonLegacy
            dropDirection={'left'}
            dropDownItems={operatorDropDownItems}
            selectDropDownItem={(value) => {
              updateTarget(target.rule_id, target.target_id, {
                conditional_operator: value
              });
            }}
          >
            <SimpleButtonContainerContent
              additionalContainerStyling="bg-white rounded border border-neutral-200"
              widthClass="w-full 2xl:w-[250px]"
              dropdownLabel={
                operatorDropDownItems.find(
                  (operator) => target.conditional_operator === operator.key
                )?.label ?? operatorDropDownItems[0].label
              }
            />
          </DropDownButtonLegacy>
        </div>

        {/* Target Key (if applicable)  */}
        {isRuleTargetKeyValue(target) && (
          <div className="flex flex-col">
            <Textbox
              placeholder="Target Key"
              errors={[]}
              value={target.key ?? ''}
              onChange={(key) =>
                updateTarget(target.rule_id, target.target_id, { key })
              }
              identifier={`targetkey-textbox-targetid-${target.target_id}-ruleid-${target.rule_id}`}
            />
          </div>
        )}

        {/* Target Value */}
        <div
          className={`flex flex-col ${isRuleTargetKeyValue(target) ? '' : ''}`}
        >
          <Textbox
            identifier={`targetvalue-textbox-targetid-${target.target_id}-ruleid-${target.rule_id}`}
            placeholder="Target Value"
            errors={[]}
            value={target.value}
            onChange={(value) =>
              updateTarget(target.rule_id, target.target_id, { value })
            }
          />
        </div>
        {/* Remove Button */}
        <div className="flex flex-col justify-center">
          <Button
            onClick={() => removeTarget(target.target_id, target.rule_id)}
            isDisabled={!canRemoveTarget(target.rule_id)}
            theme={ButtonTheme.MINIMAL}
            identifier={`targetdelete-btn-targetid-${target.target_id}-ruleid-${target.rule_id}`}
            classOverrides={`text-xl block ${
              targets.length === 1 ? '' : 'hover:text-error-300'
            }`}
          >
            <i className="flex items-center ra-icons-generic-bin"></i>
          </Button>
        </div>
      </div>
    </div>
  );
}
